import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useCallback } from 'react';
import { format, parse, isValid } from 'date-fns';
import Grid from '../Grid';
import Input from '../Input';
import { CopyIcon as Copy } from '../icons/CopyIcon';
import { CheckIcon as Check } from '../icons/CheckIcon';
import Toaster from '../Toaster';
import { StyledSelectedDateHeader, StyledSelectedDateRow, StyledSelectedDateDot, StyledSelectedDateLabel, StyledCopyButton, StyledDateInputWrapper, StyledDateRangeInputs, StyledDateRangeInputItem, StyledDateRangeSeparator, StyledHeaderRows, StyledHeaderShadow, StyledSelectedDateWeekDays, StyledSelectedDateWeekDay, StyledWeekDay, StyledErrorRow, StyledErrorDot, StyledErrorText } from './styles';
import type { DateRange } from './types';

// Indexed by Date#getDay(): 0 = Sunday … 6 = Saturday. Rotate with firstDayOfWeek for column headers.
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PARSE_FORMATS = ['MMM do, yyyy', 'MMMM do, yyyy', 'MMMM d, yyyy', 'MMM d, yyyy', 'MM/dd/yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd', 'MM-dd-yyyy'];
const DISPLAY_FORMAT = 'MMM do, yyyy';
const formatDate = (date: Date | null | undefined): string => date == null ? '' : format(date, DISPLAY_FORMAT);
const tryParseDate = (value: string): Date | null => {
  for (const fmt of PARSE_FORMATS) {
    const parsed = parse(value, fmt, new Date());
    if (isValid(parsed)) return parsed;
  }
  return null;
};
interface EditableDateInputProps {
  name: string;
  ariaLabel: string;
  value: Date | null | undefined;
  placeholder?: string;
  disableTextEdit: boolean;
  color?: 'success' | 'error';
  onCommit: (date: Date) => void;
  onValidityChange?: (isValid: boolean) => void;
}
const EditableDateInput = ({
  name,
  ariaLabel,
  value,
  placeholder = format(new Date(), DISPLAY_FORMAT),
  disableTextEdit,
  color,
  onCommit,
  onValidityChange
}: EditableDateInputProps): React.ReactElement => {
  const [text, setText] = useState(formatDate(value));
  const [isEditing, setIsEditing] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  useEffect(() => {
    if (!isEditing) setText(formatDate(value));
  }, [value, isEditing]);
  const resetError = useCallback((): void => {
    setIsInvalid(false);
    onValidityChange?.(true);
  }, [onValidityChange]);
  const commit = useCallback((raw: string): void => {
    setIsEditing(false);
    resetError();
    const parsed = tryParseDate(raw);
    if (parsed != null) {
      onCommit(parsed);
      setText(formatDate(parsed));
      return;
    }
    setText(formatDate(value));
  }, [onCommit, resetError, value]);
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.value;
    setText(next);
    if (next.trim() === '') {
      setIsInvalid(false);
      onValidityChange?.(true);
    } else {
      const isValid = tryParseDate(next) !== null;
      setIsInvalid(!isValid);
      onValidityChange?.(isValid);
    }
  }, [onValidityChange]);
  return <Input name={name} type="text" value={text} onChange={handleChange} onFocus={() => {
    setIsEditing(true);
  }} onBlur={(e_0: React.FocusEvent<HTMLInputElement>) => {
    commit(e_0.target.value);
  }} onKeyDown={(e_1: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_1.key === 'Enter') {
      commit((e_1.target as HTMLInputElement).value);
      (e_1.target as HTMLInputElement).blur();
    }
    if (e_1.key === 'Escape') {
      setIsEditing(false);
      resetError();
      setText(formatDate(value));
      (e_1.target as HTMLInputElement).blur();
    }
  }} placeholder={placeholder} color={isInvalid ? 'error' : color} inputProps={{
    readOnly: disableTextEdit,
    spellCheck: false,
    'aria-label': ariaLabel
  }} />;
};
interface DateHeaderRowProps {
  label: string;
  variant: 'primary' | 'comparison';
  /** Single-date mode value (used when range is not provided). */
  date?: Date | null;
  /** Range value — when provided, the row renders two from/to inputs. */
  range?: DateRange | null;
  /** Single-date commit handler. */
  onDateChange?: (date: Date) => void;
  /** Range commit handler — receives the full range each time either endpoint is committed. */
  onRangeChange?: (range: DateRange) => void;
  /** Render two fields (from/to) even when range is null/empty — both empty initially. */
  asRange?: boolean;
  disableTextEdit: boolean;
  color?: 'success' | 'error';
  inputName: string;
}
const DateHeaderRow = t0 => {
  const $ = _c(50);
  const {
    label,
    variant,
    date,
    range,
    onDateChange,
    onRangeChange,
    asRange: t1,
    disableTextEdit,
    color,
    inputName
  } = t0;
  const asRange = t1 === undefined ? false : t1;
  const isRangeRow = asRange || range != null;
  const fromValue = range?.from ?? null;
  const toValue = range?.to ?? null;
  let t2;
  if ($[0] !== toValue) {
    t2 = toValue == null ? "" : ` – ${formatDate(toValue)}`;
    $[0] = toValue;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toSuffix = t2;
  let t3;
  if ($[2] !== date || $[3] !== fromValue || $[4] !== isRangeRow || $[5] !== toSuffix) {
    t3 = isRangeRow ? `${formatDate(fromValue)}${toSuffix}` : formatDate(date);
    $[2] = date;
    $[3] = fromValue;
    $[4] = isRangeRow;
    $[5] = toSuffix;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const copyValue = t3;
  const [toastOpen, setToastOpen] = useState(false);
  const [fromInvalid, setFromInvalid] = useState(false);
  const [toInvalid, setToInvalid] = useState(false);
  const hasInvalidInput = fromInvalid || toInvalid;
  const toastDescription = variant === "comparison" ? "The comparison date range has been successfully copied to your clipboard." : "The date range has been successfully copied to your clipboard.";
  let t4;
  if ($[7] !== copyValue) {
    t4 = () => {
      if (copyValue === "" || navigator.clipboard == null) {
        return;
      }
      navigator.clipboard.writeText(copyValue);
      setToastOpen(true);
    };
    $[7] = copyValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleCopy = t4;
  let t5;
  if ($[9] !== onRangeChange || $[10] !== toValue) {
    t5 = next => {
      if (onRangeChange == null) {
        return;
      }
      const to = toValue ?? undefined;
      onRangeChange(to == null ? {
        from: next
      } : {
        from: next,
        to
      });
    };
    $[9] = onRangeChange;
    $[10] = toValue;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const commitFrom = t5;
  let t6;
  if ($[12] !== fromValue || $[13] !== onRangeChange) {
    t6 = next_0 => {
      if (onRangeChange == null) {
        return;
      }
      if (fromValue == null) {
        onRangeChange({
          from: next_0
        });
        return;
      }
      onRangeChange({
        from: fromValue,
        to: next_0
      });
    };
    $[12] = fromValue;
    $[13] = onRangeChange;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const commitTo = t6;
  let t7;
  if ($[15] !== variant) {
    t7 = <StyledSelectedDateDot $variant={variant} />;
    $[15] = variant;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== label) {
    t8 = <StyledSelectedDateLabel variant="h5">{label}</StyledSelectedDateLabel>;
    $[17] = label;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = `Copy ${label}`;
  const t10 = `Copy ${label}`;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Copy />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== handleCopy || $[21] !== t10 || $[22] !== t9) {
    t12 = <StyledCopyButton type="button" onClick={handleCopy} aria-label={t9} title={t10}>{t11}</StyledCopyButton>;
    $[20] = handleCopy;
    $[21] = t10;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t7 || $[26] !== t8) {
    t13 = <StyledSelectedDateRow>{t7}{t8}{t12}</StyledSelectedDateRow>;
    $[24] = t12;
    $[25] = t7;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setToastOpen(false);
    };
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Check />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== toastDescription || $[31] !== toastOpen) {
    t16 = <Toaster variant="success" open={toastOpen} onClose={t14} headerMessage="Woho!" headerIcon={t15} description={toastDescription} successDelay={1000} />;
    $[30] = toastDescription;
    $[31] = toastOpen;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== color || $[34] !== commitFrom || $[35] !== commitTo || $[36] !== date || $[37] !== disableTextEdit || $[38] !== fromValue || $[39] !== hasInvalidInput || $[40] !== inputName || $[41] !== isRangeRow || $[42] !== label || $[43] !== onDateChange || $[44] !== toValue) {
    t17 = isRangeRow ? <><StyledDateRangeInputs $hasError={hasInvalidInput}><StyledDateRangeInputItem><EditableDateInput name={`${inputName}-from`} ariaLabel={`${label} start date`} value={fromValue} disableTextEdit={disableTextEdit} color={color} onCommit={commitFrom} onValidityChange={isValid => {
            setFromInvalid(!isValid);
          }} /></StyledDateRangeInputItem><StyledDateRangeSeparator variant="body3">–</StyledDateRangeSeparator><StyledDateRangeInputItem><EditableDateInput name={`${inputName}-to`} ariaLabel={`${label} end date`} value={toValue} disableTextEdit={disableTextEdit} color={color} onCommit={commitTo} onValidityChange={isValid_0 => {
            setToInvalid(!isValid_0);
          }} /></StyledDateRangeInputItem></StyledDateRangeInputs>{hasInvalidInput && <StyledErrorRow><StyledErrorDot /><StyledErrorText variant="body3">Enter a day, month, and year.</StyledErrorText></StyledErrorRow>}</> : <><StyledDateInputWrapper $hasError={hasInvalidInput}><EditableDateInput name={inputName} ariaLabel={label} value={date} disableTextEdit={disableTextEdit || onDateChange == null} color={color} onCommit={next_1 => onDateChange?.(next_1)} onValidityChange={isValid_1 => {
          setFromInvalid(!isValid_1);
        }} /></StyledDateInputWrapper>{hasInvalidInput && <StyledErrorRow><StyledErrorDot /><StyledErrorText variant="body3">Enter a day, month, and year.</StyledErrorText></StyledErrorRow>}</>;
    $[33] = color;
    $[34] = commitFrom;
    $[35] = commitTo;
    $[36] = date;
    $[37] = disableTextEdit;
    $[38] = fromValue;
    $[39] = hasInvalidInput;
    $[40] = inputName;
    $[41] = isRangeRow;
    $[42] = label;
    $[43] = onDateChange;
    $[44] = toValue;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t13 || $[47] !== t16 || $[48] !== t17) {
    t18 = <Grid>{t13}{t16}{t17}</Grid>;
    $[46] = t13;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
};
interface SelectedDateHeaderProps {
  selectedDate?: Date | null;
  selectedRange?: DateRange | null;
  /** Force the selected row to render two fields (start + stop) even when no range is set yet. */
  selectedAsRange?: boolean;
  onDateChange: (date: Date) => void;
  onSelectedRangeChange?: (range: DateRange) => void;
  /** When true, render a second "Compared to" row below the selected date row. */
  showComparison?: boolean;
  comparisonDate?: Date | null;
  comparisonRange?: DateRange | null;
  /** Force the comparison row to render two fields when the comparison side is range-based. */
  comparisonAsRange?: boolean;
  onComparisonDateChange?: (date: Date) => void;
  onComparisonRangeChange?: (range: DateRange) => void;
  disableTextEdit?: boolean;
  color?: 'success' | 'error';
  firstDayOfWeek?: 0 | 1;
}
const SelectedDateHeader = t0 => {
  const $ = _c(30);
  const {
    selectedDate,
    selectedRange,
    selectedAsRange: t1,
    onDateChange,
    onSelectedRangeChange,
    showComparison: t2,
    comparisonDate,
    comparisonRange,
    comparisonAsRange: t3,
    onComparisonDateChange,
    onComparisonRangeChange,
    disableTextEdit: t4,
    color,
    firstDayOfWeek: t5
  } = t0;
  const selectedAsRange = t1 === undefined ? false : t1;
  const showComparison = t2 === undefined ? false : t2;
  const comparisonAsRange = t3 === undefined ? false : t3;
  const disableTextEdit = t4 === undefined ? false : t4;
  const firstDayOfWeek = t5 === undefined ? 1 : t5;
  let t6;
  if ($[0] !== firstDayOfWeek) {
    t6 = [...WEEK_DAYS.slice(firstDayOfWeek), ...WEEK_DAYS.slice(0, firstDayOfWeek)];
    $[0] = firstDayOfWeek;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const weekDays = t6;
  let t7;
  if ($[2] !== color || $[3] !== disableTextEdit || $[4] !== onDateChange || $[5] !== onSelectedRangeChange || $[6] !== selectedAsRange || $[7] !== selectedDate || $[8] !== selectedRange) {
    t7 = <DateHeaderRow label="Selected date" variant="primary" date={selectedDate} range={selectedRange} asRange={selectedAsRange} onDateChange={onDateChange} onRangeChange={onSelectedRangeChange} disableTextEdit={disableTextEdit} color={color} inputName="selected-date" />;
    $[2] = color;
    $[3] = disableTextEdit;
    $[4] = onDateChange;
    $[5] = onSelectedRangeChange;
    $[6] = selectedAsRange;
    $[7] = selectedDate;
    $[8] = selectedRange;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== color || $[11] !== comparisonAsRange || $[12] !== comparisonDate || $[13] !== comparisonRange || $[14] !== disableTextEdit || $[15] !== onComparisonDateChange || $[16] !== onComparisonRangeChange || $[17] !== showComparison) {
    t8 = showComparison && <DateHeaderRow label="Compared to" variant="comparison" date={comparisonDate} range={comparisonRange} asRange={comparisonAsRange} onDateChange={onComparisonDateChange} onRangeChange={onComparisonRangeChange} disableTextEdit={disableTextEdit} color={color} inputName="comparison-date" />;
    $[10] = color;
    $[11] = comparisonAsRange;
    $[12] = comparisonDate;
    $[13] = comparisonRange;
    $[14] = disableTextEdit;
    $[15] = onComparisonDateChange;
    $[16] = onComparisonRangeChange;
    $[17] = showComparison;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <StyledHeaderRows>{t7}{t8}</StyledHeaderRows>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== weekDays) {
    t10 = <StyledSelectedDateWeekDays>{weekDays.map(_temp)}</StyledSelectedDateWeekDays>;
    $[22] = weekDays;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <StyledSelectedDateHeader>{t9}{t10}</StyledSelectedDateHeader>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <StyledHeaderShadow />;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11) {
    t13 = <Grid position="relative">{t11}{t12}</Grid>;
    $[28] = t11;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
SelectedDateHeader.displayName = 'SelectedDateHeader';
export default SelectedDateHeader;
function _temp(day) {
  return <StyledSelectedDateWeekDay key={day}><StyledWeekDay variant="body3">{day}</StyledWeekDay></StyledSelectedDateWeekDay>;
}